/**
 * @loop3/ui — Loop3 App Registry
 *
 * Single source of truth for the list of Loop3 internal apps. Consumed by
 * <AppSwitcher> and anywhere else cross-app navigation is needed.
 *
 * Icons are imported by the consumer (kept out of this file so the registry
 * has no runtime dependency on lucide-react). Each consumer provides icons
 * when rendering the switcher.
 */

export interface Loop3App {
  id: string;
  name: string;
  url: string;
  description?: string;
  /**
   * App is only visible to admins. <AppSwitcher> hides these unless it
   * receives `isAdmin`. Single source of truth for cross-app admin gating —
   * consumers no longer hand-filter the registry.
   */
  adminOnly?: boolean;
}

export const LOOP3_APPS: readonly Loop3App[] = [
  {
    id: 'platform',
    name: 'L3OS',
    url: 'https://platform.loop3.ai',
    description: 'Internal tools & integrations',
  },
  {
    id: 'admin',
    name: 'Admin',
    url: 'https://admin.loop3.ai',
    description: 'Allocations, finance, and internal operations',
    adminOnly: true,
  },
  {
    id: 'timesheet',
    name: 'Timesheet',
    url: 'https://timesheet.loop3.ai',
    description: 'Track hours per client per week',
  },
  {
    id: 'timeoff',
    name: 'Time Off',
    url: 'https://timeoff.loop3.ai',
    description: 'Vacation, sick, and personal time off',
  },
  {
    id: 'calendar',
    name: 'Calendar',
    url: 'https://calendar.loop3.ai/app',
    description: 'Booking links for you and your client squads',
  },
  {
    id: 'meetings',
    name: 'Meetings Hub',
    url: 'https://meetings.loop3.ai',
    description: 'Meeting recordings, summaries, and follow-ups',
  },
  {
    id: 'demos',
    name: 'Demos',
    url: 'https://demos.loop3.ai',
    description: 'Product demos for venture firms, on fictional data',
  },
] as const;

export function getLoop3App(id: string): Loop3App | undefined {
  return LOOP3_APPS.find((a) => a.id === id);
}

/**
 * The apps a user may see in the switcher. Non-admins never see `adminOnly`
 * apps. Single source of truth for cross-app admin gating — every consumer
 * goes through this instead of hand-filtering the registry (which led to the
 * admin app leaking into non-OS app switchers).
 */
export function visibleAppsForUser(
  isAdmin: boolean,
  apps: readonly Loop3App[] = LOOP3_APPS,
): readonly Loop3App[] {
  return isAdmin ? apps : apps.filter((a) => !a.adminOnly);
}
